// SCR-STAFF-403 (بخش AP-BIL-02؛ US-BIL-050، 051): ابطال صورتحساب با دلیل و تأیید approval.invoice.void (فقط همان روز و شیفت باز — C-AGENT-563؛ مانع ←
// پیشنهاد اعلامیه بستانکاری)، اعلامیه بستانکاری جزئی به ازای قلم تا سقف قابل اعتبار، فهرست پرداخت‌ها و اعلامیه‌های صادرشده.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type BilInvoiceView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { FormDialog, JalaliDate, ReasonDialog } from '@phoenix/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useApproval } from '../iam/useApproval';
import { parseRials } from './CashierPage';
import { Amount, rial } from './common';

export function InvoiceCorrections({ invoice, onChanged }: { invoice: BilInvoiceView; onChanged: () => void }) {
  const { t } = useTranslation('bil');
  const { tenantApi } = useTenantSession();
  const access = useAccess();
  const key = useIdempotencyKey();
  const { requireApproval, dialog: approvalDialog } = useApproval();
  const [voidOpen, setVoidOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditReason, setCreditReason] = useState('');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const actions = invoice.actions;
  const full = access.accessMode === 'Full';

  const voidInvoice = useMutation({
    mutationFn: async (reason: string) => {
      const body = { reason, approvalId: null as string | null };
      const send = () =>
        unwrap(tenantApi.POST('/api/v1/billing/invoices/{id}:void', { params: { path: { id: invoice.id } }, body, headers: { 'Idempotency-Key': key.current() } }));
      try {
        return await send();
      } catch (error) {
        if (isApiError(error) && error.code === 'PHX-BIL-0209' && typeof error.extensions['key'] === 'string') {
          key.settle(error);
          const outcome = await requireApproval({ key: error.extensions['key'] as string, amount: invoice.totals.payable.amount, context: { invoiceId: invoice.id } });
          if (outcome.status === 'approved' || outcome.status === 'requested') {
            body.approvalId = outcome.approvalId;
            return send();
          }
        }
        throw error;
      }
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setVoidOpen(false);
      onChanged();
    },
  });
  const creditNote = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/invoices/{id}/credit-notes', {
          params: { path: { id: invoice.id } },
          body: {
            reason: creditReason.trim(),
            lines: Object.entries(amounts)
              .filter(([, value]) => parseRials(value) > 0)
              .map(([invoiceLineId, value]) => ({ invoiceLineId, amount: rial(parseRials(value)) })),
          },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setCreditOpen(false);
      setAmounts({});
      setCreditReason('');
      onChanged();
    },
  });

  const voidBlocked = voidInvoice.error && isApiError(voidInvoice.error) && voidInvoice.error.code === 'PHX-BIL-0601';
  return (
    <>
      {invoice.voidedAt ? (
        <Alert severity="warning" data-testid="bil-invoice-voided">
          {t('bil:corrections_ui.voided', { reason: invoice.voidReason ?? '' })} <JalaliDate value={invoice.voidedAt} withTime />
        </Alert>
      ) : null}
      {actions && full ? (
        <Stack direction="row" spacing={1}>
          {access.can('bil.invoice.void') ? (
            <Button variant="outlined" color="error" onClick={() => setVoidOpen(true)} disabled={!actions.canVoid} data-testid="bil-invoice-void">
              {t('bil:corrections_ui.void')}
            </Button>
          ) : null}
          {access.can('bil.credit_note.issue') ? (
            <Button variant="outlined" onClick={() => setCreditOpen(true)} disabled={!actions.canCredit} data-testid="bil-invoice-credit">
              {t('bil:corrections_ui.credit_note')}
            </Button>
          ) : null}
          {!actions.canVoid && actions.voidBlockedReason ? (
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }} data-testid="bil-void-blocked">
              {t(`bil:corrections_ui.blocked.${actions.voidBlockedReason}`, { defaultValue: actions.voidBlockedReason })}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
      {(invoice.payments?.length ?? 0) > 0 || (invoice.creditNotes?.length ?? 0) > 0 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('bil:corrections_ui.payments')}
          </Typography>
          {(invoice.payments ?? []).map((payment) => (
            <Stack key={`${payment.paymentId}-${payment.amount.amount}`} direction="row" spacing={2} data-testid="bil-invoice-payment">
              <JalaliDate value={payment.receivedAt} withTime />
              <Typography>{t(`bil:method.${payment.method}`)}</Typography>
              <Amount money={payment.amount} />
            </Stack>
          ))}
          {(invoice.creditNotes ?? []).map((note) => (
            <Stack key={note.id} direction="row" spacing={2} data-testid="bil-invoice-credit-note">
              <bdi dir="ltr">{note.number}</bdi>
              <Typography>{note.reason}</Typography>
              <Amount money={note.amount} />
            </Stack>
          ))}
        </Paper>
      ) : null}
      <ReasonDialog
        open={voidOpen}
        titleKey="bil:corrections_ui.void"
        messageKey="bil:corrections_ui.void_message"
        busy={voidInvoice.isPending}
        error={voidInvoice.error}
        onConfirm={(reason) => voidInvoice.mutate(reason)}
        onCancel={() => setVoidOpen(false)}
      >
        {voidBlocked ? (
          <Alert severity="info" data-testid="bil-void-suggest-credit">
            {t('bil:corrections_ui.suggest_credit')}
          </Alert>
        ) : null}
      </ReasonDialog>
      <FormDialog
        open={creditOpen}
        titleKey="bil:corrections_ui.credit_note"
        onSubmit={() => creditNote.mutate()}
        onClose={() => setCreditOpen(false)}
        isDirty={creditReason !== ''}
        isSubmitting={creditNote.isPending}
        submitError={creditNote.error}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          {actions ? (
            <Typography variant="body2">
              {t('bil:corrections_ui.creditable')} <Amount money={actions.creditable} />
            </Typography>
          ) : null}
          {invoice.lines.map((line) => (
            <TextField
              key={line.id}
              size="small"
              label={line.title}
              value={amounts[line.id] ?? ''}
              onChange={(event) => setAmounts({ ...amounts, [line.id]: event.target.value })}
              slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-credit-line-amount' } }}
            />
          ))}
          <TextField required label={t('bil:finance.reason')} value={creditReason} onChange={(event) => setCreditReason(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'bil-credit-reason' } }} />
        </Stack>
      </FormDialog>
      {approvalDialog}
    </>
  );
}
